import React, { useState, useEffect } from 'react';
import { 
  Download, Filter, RotateCcw, BarChart3, TrendingUp, 
  DollarSign, Users, Target, Calendar, Search, UserCheck,
  Heart, Star, Clock, Activity, MapPin, Building2, 
  Award, Zap, Shield, Globe, Phone, Mail, MessageSquare
} from 'lucide-react';

const CustomerAnalytics = () => {
  // State management
  const [filters, setFilters] = useState({
    dateRange: '30',
    segment: 'all',
    industry: 'all',
    region: 'all',
    engagement: 'all',
    search: ''
  });

  const [customerData, setCustomerData] = useState([]);
  const [kpis, setKpis] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    newCustomers: 0,
    avgSatisfaction: 0,
    avgLifetimeValue: 0,
    churnRate: 0,
    engagementScore: 0,
    revenueGrowth: 0
  });

  // Filter handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilter = () => {
    console.log('Applying filters:', filters);
    loadCustomerData();
  };

  const handleResetFilter = () => {
    setFilters({
      dateRange: '30',
      segment: 'all',
      industry: 'all',
      region: 'all',
      engagement: 'all',
      search: ''
    });
    loadCustomerData();
  };

  // Export handlers
  const handleExportPDF = () => {
    console.log('Exporting Customer Analytics to PDF');
  };

  const handleExportExcel = () => {
    console.log('Exporting Customer Analytics to Excel');
  };

  // Load data
  useEffect(() => {
    loadCustomerData();
  }, []);

  const loadCustomerData = () => {
    // Dummy data for Customer Analytics
    const dummyCustomers = [
      {
        id: 'CUST-001',
        companyName: 'TechCorp Solutions',
        contactPerson: 'John Smith',
        email: 'john@techcorp.com',
        phone: '+1-555-0123',
        industry: 'Technology',
        region: 'North America',
        segment: 'Enterprise',
        status: 'Active',
        satisfaction: 4.8,
        lifetimeValue: 250000,
        engagementScore: 92,
        lastActivity: '2024-01-22',
        totalOrders: 15,
        avgOrderValue: 16500,
        customerSince: '2022-03-15',
        location: 'New York, NY',
        employees: '500-1000',
        revenue: 50000000
      },
      {
        id: 'CUST-002',
        companyName: 'Global Manufacturing Inc',
        contactPerson: 'Maria Garcia',
        email: 'maria@globalmfg.com',
        phone: '+1-555-0456',
        industry: 'Manufacturing',
        region: 'North America',
        segment: 'Mid-Market',
        status: 'Active',
        satisfaction: 4.6,
        lifetimeValue: 180000,
        engagementScore: 87,
        lastActivity: '2024-01-20',
        totalOrders: 12,
        avgOrderValue: 15000,
        customerSince: '2022-08-10',
        location: 'Chicago, IL',
        employees: '200-500',
        revenue: 25000000
      },
      {
        id: 'CUST-003',
        companyName: 'RetailMax Stores',
        contactPerson: 'David Wilson',
        email: 'david@retailmax.com',
        phone: '+1-555-0789',
        industry: 'Retail',
        region: 'North America',
        segment: 'SMB',
        status: 'At Risk',
        satisfaction: 3.2,
        lifetimeValue: 45000,
        engagementScore: 45,
        lastActivity: '2024-01-05',
        totalOrders: 8,
        avgOrderValue: 5600,
        customerSince: '2023-01-20',
        location: 'Los Angeles, CA',
        employees: '50-200',
        revenue: 5000000
      },
      {
        id: 'CUST-004',
        companyName: 'HealthCare Plus',
        contactPerson: 'Dr. Emily Davis',
        email: 'emily@healthcareplus.com',
        phone: '+1-555-0321',
        industry: 'Healthcare',
        region: 'North America',
        segment: 'Enterprise',
        status: 'Active',
        satisfaction: 4.9,
        lifetimeValue: 320000,
        engagementScore: 95,
        lastActivity: '2024-01-22',
        totalOrders: 22,
        avgOrderValue: 14500,
        customerSince: '2021-11-05',
        location: 'Boston, MA',
        employees: '1000+',
        revenue: 75000000
      },
      {
        id: 'CUST-005',
        companyName: 'FinanceFirst Bank',
        contactPerson: 'Robert Taylor',
        email: 'robert@financefirst.com',
        phone: '+1-555-0654',
        industry: 'Finance',
        region: 'North America',
        segment: 'Enterprise',
        status: 'Active',
        satisfaction: 4.7,
        lifetimeValue: 280000,
        engagementScore: 89,
        lastActivity: '2024-01-21',
        totalOrders: 18,
        avgOrderValue: 15500,
        customerSince: '2022-01-15',
        location: 'Miami, FL',
        employees: '500-1000',
        revenue: 40000000
      },
      {
        id: 'CUST-006',
        companyName: 'EduTech Academy',
        contactPerson: 'Sarah Johnson',
        email: 'sarah@edutech.com',
        phone: '+1-555-0987',
        industry: 'Education',
        region: 'North America',
        segment: 'Mid-Market',
        status: 'Churned',
        satisfaction: 2.8,
        lifetimeValue: 25000,
        engagementScore: 25,
        lastActivity: '2023-12-15',
        totalOrders: 5,
        avgOrderValue: 5000,
        customerSince: '2023-06-01',
        location: 'Seattle, WA',
        employees: '100-200',
        revenue: 8000000
      }
    ];

    setCustomerData(dummyCustomers);

    // Calculate KPIs
    const totalCustomers = dummyCustomers.length;
    const activeCustomers = dummyCustomers.filter(customer => customer.status === 'Active').length;
    const newCustomers = dummyCustomers.filter(customer => {
      const customerSince = new Date(customer.customerSince);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return customerSince > thirtyDaysAgo;
    }).length;
    const avgSatisfaction = (dummyCustomers.reduce((sum, customer) => sum + customer.satisfaction, 0) / totalCustomers).toFixed(1);
    const avgLifetimeValue = dummyCustomers.reduce((sum, customer) => sum + customer.lifetimeValue, 0) / totalCustomers;
    const churnRate = ((dummyCustomers.filter(customer => customer.status === 'Churned').length / totalCustomers) * 100).toFixed(1);
    const avgEngagementScore = (dummyCustomers.reduce((sum, customer) => sum + customer.engagementScore, 0) / totalCustomers).toFixed(0);
    const revenueGrowth = 12.5; // Dummy growth percentage

    setKpis({
      totalCustomers,
      activeCustomers,
      newCustomers,
      avgSatisfaction,
      avgLifetimeValue,
      churnRate,
      engagementScore: avgEngagementScore,
      revenueGrowth
    });
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': { color: '#10b981', bg: '#d1fae5', icon: UserCheck },
      'At Risk': { color: '#f59e0b', bg: '#fef3c7', icon: Activity },
      'Churned': { color: '#ef4444', bg: '#fee2e2', icon: Users },
      'Inactive': { color: 'var(--color-text-secondary, #5B6478)', bg: '#f3f4f6', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig['Active'];
    const IconComponent = config.icon;

    return (
      <span 
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '4px',
          padding: '4px 8px', 
          borderRadius: '12px', 
          fontSize: '11px', 
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color
        }}
      >
        <IconComponent size={10} />
        {status}
      </span>
    );
  };

  const getSegmentBadge = (segment) => {
    const segmentConfig = {
      'Enterprise': { color: '#E83E9C', bg: '#ede9fe' },
      'Mid-Market': { color: '#059669', bg: '#d1fae5' },
      'SMB': { color: '#dc2626', bg: '#fee2e2' }
    };

    const config = segmentConfig[segment] || segmentConfig['Mid-Market'];

    return (
      <span 
        style={{ 
          padding: '2px 6px', 
          borderRadius: '8px', 
          fontSize: '10px', 
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color
        }}
      >
        {segment}
      </span>
    );
  };

  const getEngagementBadge = (score) => {
    let color, bg;
    if (score >= 80) {
      color = '#10b981';
      bg = '#d1fae5';
    } else if (score >= 60) {
      color = '#f59e0b';
      bg = '#fef3c7';
    } else {
      color = '#ef4444';
      bg = '#fee2e2';
    }

    return (
      <span 
        style={{ 
          padding: '2px 6px', 
          borderRadius: '8px', 
          fontSize: '10px', 
          fontWeight: '500',
          backgroundColor: bg,
          color: color
        }}
      >
        {score}%
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="sales-dashboard" style={{ fontSize: '14px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Customer Analytics</h1>
          <p>Comprehensive customer behavior analysis and engagement insights</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={handleExportPDF}>
            <Download size={16} />
            Export PDF
          </button>
          <button className="btn-primary" onClick={handleExportExcel}>
            <Download size={16} />
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="form-section">
        <div className="section-header" style={{ marginLeft: '20px' }}>
          <h3>Report Filters</h3>
          <p>Customize your customer analytics and insights</p>
        </div>
        <div className="form-content">
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Date Range</label>
              <select 
                className="form-select" 
                name="dateRange" 
                value={filters.dateRange}
                onChange={handleInputChange}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Customer Segment</label>
              <select 
                className="form-select" 
                name="segment" 
                value={filters.segment}
                onChange={handleInputChange}
              >
                <option value="all">All Segments</option>
                <option value="Enterprise">Enterprise</option>
                <option value="Mid-Market">Mid-Market</option>
                <option value="SMB">SMB</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select 
                className="form-select" 
                name="industry" 
                value={filters.industry}
                onChange={handleInputChange}
              >
                <option value="all">All Industries</option>
                <option value="Technology">Technology</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Retail">Retail</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Finance">Finance</option>
                <option value="Education">Education</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Region</label>
              <select 
                className="form-select" 
                name="region" 
                value={filters.region}
                onChange={handleInputChange}
              >
                <option value="all">All Regions</option>
                <option value="North America">North America</option>
                <option value="Europe">Europe</option>
                <option value="Asia Pacific">Asia Pacific</option>
                <option value="Latin America">Latin America</option>
              </select>
            </div>
          </div>
          <div className="grid-row-2">
            <div className="form-group">
              <label className="form-label">Engagement Level</label>
              <select 
                className="form-select" 
                name="engagement" 
                value={filters.engagement}
                onChange={handleInputChange}
              >
                <option value="all">All Levels</option>
                <option value="high">High (80%+)</option>
                <option value="medium">Medium (60-79%)</option>
                <option value="low">Low (under 60%)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Search Customers</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  name="search"
                  value={filters.search}
                  onChange={handleInputChange}
                  placeholder="Search by company, contact, or email..."
                  style={{ paddingLeft: '40px' }}
                />
                <Search 
                  size={16} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: 'var(--color-text-secondary, #5B6478)' 
                  }} 
                />
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button onClick={handleApplyFilter} className="btn-primary">
              <Filter size={16} /> Apply Filter
            </button>
            <button onClick={handleResetFilter} className="btn-secondary">
              <RotateCcw size={16} /> Reset Filter
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.totalCustomers}</div>
          <div className="metric-title">Total Customers</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <UserCheck size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.activeCustomers}</div>
          <div className="metric-title">Active Customers</div>
          <div className="metric-subtitle">currently engaged</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Heart size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.newCustomers}</div>
          <div className="metric-title">New Customers</div>
          <div className="metric-subtitle">this month</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Star size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+0.3</span>
            </div>
          </div>
          <div className="metric-value">{kpis.avgSatisfaction}/5</div>
          <div className="metric-title">Avg Satisfaction</div>
          <div className="metric-subtitle">customer rating</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.avgLifetimeValue)}</div>
          <div className="metric-title">Avg Lifetime Value</div>
          <div className="metric-subtitle">per customer</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>-2%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.churnRate}%</div>
          <div className="metric-title">Churn Rate</div>
          <div className="metric-subtitle">lower is better</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Zap size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+7%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.engagementScore}%</div>
          <div className="metric-title">Engagement Score</div>
          <div className="metric-subtitle">overall activity</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <BarChart3 size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{kpis.revenueGrowth}%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.revenueGrowth}%</div>
          <div className="metric-title">Revenue Growth</div>
          <div className="metric-subtitle">year over year</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="form-section">
        <div className="section-header" style={{ marginLeft: '20px' }}>
          <h3>Customer Analytics Details</h3>
          <p>Detailed view of all customers and their engagement metrics</p>
        </div>
        <div className="form-content">
          <div className="data-table-container" style={{ fontSize: '13px' }}>
            <table className="data-table" style={{ fontSize: '13px', width: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Customer Information</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Segment</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Industry</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Satisfaction</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Lifetime Value</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Engagement</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Last Activity</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Customer Since</th>
                </tr>
              </thead>
              <tbody>
                {customerData.map((customer) => (
                  <tr key={customer.id} style={{ fontSize: '13px' }}>
                    <td style={{ padding: '12px 8px', minWidth: '200px' }}>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '13px', lineHeight: '1.3' }}>
                          {customer.companyName}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px' }}>
                          {customer.contactPerson} • {customer.id}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={10} />
                          {customer.email}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={10} />
                          {customer.phone}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPin size={10} />
                          {customer.location}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Building2 size={10} />
                          {customer.employees} employees
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getStatusBadge(customer.status)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getSegmentBadge(customer.segment)}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {customer.industry}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Star size={12} style={{ color: '#fbbf24' }} />
                        {customer.satisfaction}/5
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', fontWeight: '500' }}>
                      {formatCurrency(customer.lifetimeValue)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getEngagementBadge(customer.engagementScore)}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {customer.lastActivity}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {customer.customerSince}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.avgLifetimeValue * kpis.totalCustomers)}</div>
          <div className="metric-title">Total Customer Value</div>
          <div className="metric-subtitle">Combined lifetime value</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.totalCustomers}</div>
          <div className="metric-title">Total Customers</div>
          <div className="metric-subtitle">In database</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Star size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.avgSatisfaction}/5</div>
          <div className="metric-title">Avg Satisfaction</div>
          <div className="metric-subtitle">Customer happiness</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Zap size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.engagementScore}%</div>
          <div className="metric-title">Avg Engagement</div>
          <div className="metric-subtitle">Customer activity</div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalytics;
